import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import UrlApis from 'utils/api/urlApis'
import Button from '@airbus/components-react/components/Button'
import ModalNextValidator from './modalNextValidator'
import { Message, MessageSeverity } from 'constants/message'
import MessageContext from 'context/MessageContext'
import useQuery from 'utils/hooks/useQuery'
import { OrderValidator } from 'types/order'
import useMutation from 'utils/hooks/useMutation'

type Props = {
  lang: string
  idCommand: string
  actionName: string
  actionType: string
  canUserCompletlySign: boolean
  onUserActionCompleted: (action: string) => void
}

const Validation = ({
  lang,
  idCommand,
  actionName,
  actionType,
  canUserCompletlySign,
  onUserActionCompleted,
}: Props) => {
  const { t } = useTranslation()
  const { handleDisplayMessage } = useContext(MessageContext)

  // State of the modal for selecting the next validator (if required)
  const [modalNextValidatorOpened, setModalNextValidatorOpened] = useState(false)

  const [listValidators, setListValidators] = useState<OrderValidator[]>([])
  const { fetchData: fetchListValidators } = useQuery<OrderValidator[]>(UrlApis.URL_ORDER_VALIDATORS_GET)
  const { mutate: submitValidationWithValidator } = useMutation(UrlApis.URL_ORDER_VALIDATOR_PUT, '', [], true)
  const { mutate: submitValidationOneShot } = useMutation(UrlApis.URL_ORDER_VALIDATE_PUT, '', [], true)
  // Optional message (info, warning) to display
  const [messageToDisplay, setMessageToDisplay] = useState<Message>()

  /**
   * Load validators
   */
  const loadValidators = async () => {
    const responseListValidators = await fetchListValidators({
      lang: lang,
      cdnumdoc: idCommand,
    })
    const listValidatorsSortedByLevelAsc = responseListValidators?.data.sort((a, b) => a.niv - b.niv)
    setListValidators(listValidatorsSortedByLevelAsc)

    if (listValidatorsSortedByLevelAsc == undefined || listValidatorsSortedByLevelAsc.length === 0) {
      // Further validation is required : user can set-up the next validator for the order but the suggested list is empty!
      setMessageToDisplay(new Message(MessageSeverity.WARNING, '', null, t('9500148')))
    }
  }

  const submitNextValidator = async (noCommand: string, nextValidator: string) => {
    const response = await submitValidationWithValidator({
      data: {},
      params: {
        lang: lang,
        cdnumdoc: noCommand,
        validatorCode: nextValidator,
      },
      method: 'PUT',
    })

    handleDisplayMessage(
      new Message(
        MessageSeverity[response?.data?.severity.toUpperCase()],
        t('9500124') + '<' + idCommand + '>',
        response?.data?.amasisCode,
        response?.data?.description,
      ),
    )
    if (!canUserCompletlySign) {
      submitValidation(idCommand)
    }
  }

  const submitValidation = async (noCommand: string) => {
    await submitValidationOneShot({
      data: {},
      params: {
        lang: lang,
        cdnumdoc: noCommand,
      },
      method: 'PUT',
    })

    onUserActionCompleted(actionType)
  }

  const handleSubmitNextValidator = (nextValidator: string) => {
    setModalNextValidatorOpened(false)
    submitNextValidator(idCommand, nextValidator)
  }

  const handleClickValidate = () => {
    if (!canUserCompletlySign) {
      if (listValidators.length > 1) {
        // The selection of a next validator is required among a multi-valued list
        setModalNextValidatorOpened(true)
      } else if (listValidators.length === 1) {
        handleSubmitNextValidator(listValidators[0].surCode)
        // Note: if it succeeds, then the isStepNextValidatorOk is set to True which in turn submits the validation process
      } else {
        // Warning message regarding missing next validators will be displayed in modal
        setModalNextValidatorOpened(true)
      }
    } else {
      submitValidation(idCommand)
    }
  }

  useEffect(() => {
    if (idCommand && !canUserCompletlySign) {
      loadValidators()
    }
  }, [idCommand])

  return (
    <div>
      <Button variant='primary' onClick={handleClickValidate}>
        <div>{actionName}</div>
      </Button>

      <ModalNextValidator
        isOpened={modalNextValidatorOpened}
        onCloseModal={() => setModalNextValidatorOpened(false)}
        idCommand={idCommand}
        listValidators={listValidators}
        message={messageToDisplay}
        onSubmitNextValidator={handleSubmitNextValidator}
      />
    </div>
  )
}

export default Validation
